import { useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, Instagram, Loader2, CheckCircle2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/components/Reveal";
import { BRAND, EVENT_TYPES, SOCIALS } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-500 focus:border-volt/60 focus:outline-none focus:ring-1 focus:ring-volt/40 transition-colors text-base";

const initial = {
  name: "", email: "", phone: "", event_date: "", event_type: "",
  venue: "", location: "", guest_count: "", budget: "", message: "",
};

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email";
    if (form.guest_count && (isNaN(form.guest_count) || Number(form.guest_count) < 1)) errs.guest_count = "Enter a valid number";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setStatus("sending");
    try {
      const payload = { ...form, guest_count: form.guest_count ? Number(form.guest_count) : null };
      Object.keys(payload).forEach((k) => (payload[k] === "" || payload[k] === null) && delete payload[k]);
      await axios.post(`${API}/booking`, payload);
      setStatus("success");
      confetti({ particleCount: 160, spread: 85, origin: { y: 0.7 }, colors: ["#CCFF00", "#00F0FF", "#FF0055"] });
    } catch (err) {
      setStatus("idle");
      toast.error("Something glitched. Try again or reach out directly below.");
    }
  };

  const field = (key, label, type = "text", placeholder = "", testid) => (
    <div>
      <label htmlFor={`booking-${key}`} className="block font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-2">
        {label}
      </label>
      <input
        id={`booking-${key}`}
        data-testid={testid || `booking-field-${key}`}
        type={type}
        value={form[key]}
        onChange={set(key)}
        placeholder={placeholder}
        className={inputCls}
      />
      {errors[key] && <p data-testid={`booking-error-${key}`} className="mt-1.5 text-xs text-mag">{errors[key]}</p>}
    </div>
  );

  return (
    <section id="booking" data-testid="booking-section" className="relative py-20 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_30%,rgba(204,255,0,0.08),transparent_45%),radial-gradient(circle_at_85%_80%,rgba(255,0,85,0.08),transparent_45%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-12 lg:gap-16">
        <div className="lg:col-span-2">
          <SectionHead
            eyebrow="Bookings Open"
            title={<>Ready To Get <span className="text-neon">2hype?</span></>}
            sub="Let's make your event one they won't stop talking about. Tell us about the night and we'll bring the energy."
          />
          <Reveal delay={0.15} className="space-y-4">
            <a
              href={`https://wa.me/${BRAND.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-quick-call"
              className="glass rounded-xl px-5 py-4 flex items-center gap-4 hover:border-volt/50 transition-colors group"
            >
              <span className="w-11 h-11 rounded-full bg-volt/15 text-volt flex items-center justify-center group-hover:bg-volt group-hover:text-ink transition-colors">
                <Phone className="w-5 h-5" />
              </span>
              <span>
                <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">WhatsApp / Text</span>
                <span className="font-head font-bold">{BRAND.phone}</span>
              </span>
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="booking-quick-email"
              className="glass rounded-xl px-5 py-4 flex items-center gap-4 hover:border-cy/50 transition-colors group"
            >
              <span className="w-11 h-11 rounded-full bg-cy/15 text-cy flex items-center justify-center group-hover:bg-cy group-hover:text-ink transition-colors">
                <Mail className="w-5 h-5" />
              </span>
              <span>
                <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">Email</span>
                <span className="font-head font-bold">{BRAND.email}</span>
              </span>
            </a>
            <a
              href={SOCIALS[0].url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-quick-social"
              className="glass rounded-xl px-5 py-4 flex items-center gap-4 hover:border-mag/50 transition-colors group"
            >
              <span className="w-11 h-11 rounded-full bg-mag/15 text-mag flex items-center justify-center group-hover:bg-mag group-hover:text-white transition-colors">
                <Instagram className="w-5 h-5" />
              </span>
              <span>
                <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">DM The Hype</span>
                <span className="font-head font-bold">@qt2hype</span>
              </span>
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          <div className="glass rounded-3xl p-6 sm:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  data-testid="booking-success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center py-14"
                >
                  <CheckCircle2 className="w-16 h-16 text-volt mx-auto drop-shadow-[0_0_20px_rgba(204,255,0,0.5)]" />
                  <h3 className="mt-6 font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl">
                    You're In The Mix
                  </h3>
                  <p className="mt-4 text-zinc-400 max-w-md mx-auto leading-relaxed">
                    Enquiry received. QT 2hype's team will hit you back within 24 hours to lock in the date and plan the energy.
                  </p>
                  <button
                    data-testid="booking-another-button"
                    onClick={() => { setForm(initial); setStatus("idle"); }}
                    className="mt-8 glass font-head font-bold uppercase tracking-wider px-8 py-3.5 rounded-full hover:border-volt/60 hover:text-volt transition-colors"
                  >
                    Send Another Enquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="booking-form"
                  onSubmit={submit}
                  noValidate
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -16 }}
                  className="grid sm:grid-cols-2 gap-5"
                >
                  {field("name", "Name *", "text", "Your name")}
                  {field("email", "Email *", "email", "you@email.com")}
                  {field("phone", "Phone", "tel", "+44 7000 000000")}
                  {field("event_date", "Event Date", "date")}
                  <div>
                    <label htmlFor="booking-event_type" className="block font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-2">
                      Event Type
                    </label>
                    <select
                      id="booking-event_type"
                      data-testid="booking-field-event-type"
                      value={form.event_type}
                      onChange={set("event_type")}
                      className={`${inputCls} appearance-none`}
                    >
                      <option value="" className="bg-card2">Select event type</option>
                      {EVENT_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-card2">{t}</option>
                      ))}
                    </select>
                  </div>
                  {field("venue", "Venue", "text", "Venue name")}
                  {field("location", "Location", "text", "City / area")}
                  {field("guest_count", "Guest Count", "number", "e.g. 150")}
                  {field("budget", "Budget (Optional)", "text", "e.g. $1,500")}
                  <div className="sm:col-span-2">
                    <label htmlFor="booking-message" className="block font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 mb-2">
                      Message
                    </label>
                    <textarea
                      id="booking-message"
                      data-testid="booking-field-message"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about the vibe you're after..."
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div className="sm:col-span-2 pt-2">
                    <motion.button
                      type="submit"
                      data-testid="booking-submit-button"
                      disabled={status === "sending"}
                      whileHover={{ scale: status === "sending" ? 1 : 1.02 }}
                      whileTap={{ scale: status === "sending" ? 1 : 0.98 }}
                      className="w-full inline-flex items-center justify-center gap-3 bg-volt text-ink font-head font-extrabold uppercase tracking-wider px-8 py-4 rounded-full shadow-[0_0_35px_rgba(204,255,0,0.35)] hover:shadow-[0_0_50px_rgba(204,255,0,0.55)] transition-shadow disabled:opacity-70"
                    >
                      {status === "sending" ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          <CalendarCheck className="w-5 h-5" /> Book QT 2hype
                        </>
                      )}
                    </motion.button>
                    <p className="mt-4 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-600">
                      No spam. Your details only go to QT 2hype's booking team.
                    </p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
